/* eslint-disable react/jsx-filename-extension */
import { RMMClient, AccountClient, useCachedQuery } from '@nile/graphql';
import { useMutation } from '@apollo/client';
import TicketContext from '@nile/mangifera/src/Contexts/TicketContext';
import {
  GET_SHAREABLE_CLIENTS,
  GET_SHAREABLE_SITES,
  UPDATE_IT_DOC_SHARED_DETAILS
} from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import { GET_USER_ROLE_TYPES } from '@nile/banyan/src/Graphql/Mutations/role';
import {
  NoDataCard,
  Loader,
  withErrorBoundary,
  getTranslatedText,
  getParsedCrudData,
  toast,
  AccessControl,
  useProduct,
  COMMON_CONSTANTS,
  getLokaliseKeyByProduct
} from '@nile/components';
import React, { useContext, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import envConfig from '@nile/home/src/Config/env';
import { FormattedMessage, injectIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import styled from 'styled-components';
import { cloneDeep, random } from 'lodash';
import {
  Button,
  ResourceItem,
  ResourceList,
  TextStyle,
  Link,
  Heading,
  ButtonGroup,
  Modal
} from '@superops/ui-design-system';
import Select from 'react-select';
import {
  DropdownIndicator,
  formatGroupLabel,
  IndicatorSeparator
} from '@nile/components/src/Pebbles/select';
import { useCheckIsItProduct } from '@nile/components/src/Utils/helpers';

type Nullable<T> = T | null;

interface ComponentProps {
  listLoader: boolean;
  editable?: boolean;
  toggleAssociateAsset?: boolean;
  docsList: any;
  intl: any;
  match: any;
  clientId?: string;
  reRenderUpdatedData?: any;
  viewChange?: boolean;
  siteId?: string;
}
interface ClientAccount {
  name?: string;
  accountId?: string;
}
interface Site {
  id: string;
  name: string;
}
interface RoleObj {
  roleId: string;
  name: string;
}
interface DropDownObject {
  label?: string;
  value: string;
  name: string;
  accountId?: Nullable<string>;
  sharedType?: string;
  roleId?: Nullable<string>;
  id?: string;
}

interface UpdateRoleObj {
  client?: Nullable<ClientAccount>;
  accountId?: Nullable<string>;
  clientSharedType: string;
  siteSharedType: string;
  userRoleSharedType: string;
  addedRoleIds: Nullable<string[]>;
  deletedRoleIds: Nullable<string[]>;
  mappingId: string;
  roles?: Nullable<RoleObj[]>;
  siteId?: Nullable<string>;
  site?: Nullable<Site>;
}

interface ITDocList {
  client?: Nullable<ClientAccount>;
  accountId?: Nullable<string>;
  clientSharedType: string;
  siteSharedType?: string;
  userRoleSharedType: string;
  mappingId: string;
  roles: RoleObj[];
  site?: Nullable<Site>;
}
function ShareItDocumentList(props: ComponentProps) {
  const { assetId } = props?.match?.params;
  const {
    listLoader,
    docsList = [],
    editable,
    toggleAssociateAsset,
    intl,
    clientId,
    siteId,
    reRenderUpdatedData,
    viewChange
  } = props;
  const isITProduct = useCheckIsItProduct();
  const [clients, setClients] = useState([]);
  const [shareableRoles, setRoles] = useState([]);
  const [assetList, setAssetList] = useState(docsList);
  const [deleteModalActive, setDeleteModalActive] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(0);
  const [isClientChanged, setIsClientChanged] = useState(false);
  const ALL_SITES_SHARED_TYPE: string = 'AllSites';
  const ALL_SITES_LABEL: string = getTranslatedText(
    intl,
    'provisionRule.name.allSites'
  );
  const ALL_CLIENTS_SHARED_TYPE: string = isITProduct
    ? ALL_SITES_SHARED_TYPE
    : 'AllClients';
  const ALL_CLIENTS_LABEL: string = isITProduct
    ? ALL_SITES_LABEL
    : getTranslatedText(intl, 'common.all.clients');
  const ALL_ROLES_SHARED_TYPE: string = 'AllRoles';
  const ALL_ROLES_LABEL: string = getTranslatedText(intl, 'common.all.roles');
  const SITE_SHARED_TYPE: string = 'Site';
  const CLIENT_SHARED_TYPE: string = isITProduct ? SITE_SHARED_TYPE : 'Client';
  const ROLE_SHARED_TYPE: string = 'Role';
  const productType = useProduct();
  const selectedClients = assetList.map((a: UpdateRoleObj) =>
    isITProduct ? a?.site?.id : a?.client?.accountId
  );
  //const selectedSites = assetList.map((a: UpdateRoleObj) => a?.site?.id);

  useEffect(() => {
    const initObject =
      productType === COMMON_CONSTANTS.IT
        ? {
            site: {
              id: ''
            },
            roles: [],
            mappingId: random(assetList.length, 15000)
          }
        : {
            client: {
              name: '',
              accountId: ''
            },
            roles: [],
            mappingId: random(assetList.length, 15000)
          };
    const defaultShareList = editable ? [initObject] : [];
    setAssetList(docsList.length ? docsList : defaultShareList);
  }, [docsList?.length, viewChange]);

  useEffect(() => {
    if (docsList?.length && !editable) {
      const isSelectedClientPresent = docsList.find((c) =>
        isITProduct ? c?.site?.id === siteId : c.client?.accountId === clientId
      );
      if (!isSelectedClientPresent) {
        setIsClientChanged(true);
      }
    }
  }, [clientId, siteId]);
  const showWarningWhileRemove = (mappingIndex: number = 0) => {
    setDeleteModalActive((prev) => !prev);
    setDeleteIndex(() => mappingIndex);
  };
  const getAllClients = (clientsData: DropDownObject[]) => {
    const allClients = clientsData.map((c) => {
      return {
        label: c.name,
        value: isITProduct ? c.id : c.accountId,
        name: c.name,
        accountId: isITProduct ? c.id : c.accountId,
        sharedType: isITProduct ? SITE_SHARED_TYPE : CLIENT_SHARED_TYPE
      };
    });
    if (!clientId && !isITProduct) {
      // only when the IT document is not assiociated to any client, display 'All Clients'
      allClients.unshift({
        label: ALL_CLIENTS_LABEL,
        value: ALL_CLIENTS_SHARED_TYPE,
        name: ALL_CLIENTS_LABEL,
        accountId: null,
        sharedType: ALL_CLIENTS_SHARED_TYPE
      });
    }
    if (!siteId && isITProduct) {
      // only when the IT document is not assiociated to any site, display 'All Site'
      allClients.unshift({
        label: ALL_SITES_LABEL,
        value: ALL_SITES_SHARED_TYPE,
        name: ALL_SITES_LABEL,
        id: null,
        sharedType: ALL_SITES_SHARED_TYPE
      });
    }

    return allClients;
  };
  const getAllRoles = (rolesData: DropDownObject[] = []) => {
    const allRoles = rolesData.map((r) => {
      return {
        label: r?.name,
        value: r?.roleId,
        name: r?.name,
        roleId: r?.roleId,
        sharedType: ROLE_SHARED_TYPE
      };
    });
    allRoles.unshift({
      label: ALL_ROLES_LABEL,
      value: ALL_ROLES_SHARED_TYPE,
      name: ALL_ROLES_LABEL,
      sharedType: ALL_ROLES_SHARED_TYPE,
      roleId: null
    });
    return allRoles;
  };
  const [shareItDocument, { loading: shareLoading }] = useMutation(
    UPDATE_IT_DOC_SHARED_DETAILS,
    {
      client: RMMClient,
      onCompleted(res) {
        /* Close sheet container once an employee created */
        if (res?.updateITDocSharedDetails) {
          reRenderUpdatedData(res?.updateITDocSharedDetails);
        }
        if (editable) {
          toggleAssociateAsset();
          toast('success', 'rmm.share.IT.doc.success');
        } else {
          showWarningWhileRemove();
          toast('success', 'rmm.share.IT.doc.remove.success');
        }
      }
    }
  );
  const { data: shareClientData, loading: shareClientLoading } = useCachedQuery(
    GET_SHAREABLE_CLIENTS,
    {
      client: RMMClient,
      variables: {
        assetId
      },
      skip: isITProduct || !editable
    }
  );
  const { data: shareSiteData, loading: shareSiteLoading } = useCachedQuery(
    GET_SHAREABLE_SITES,
    {
      client: RMMClient,
      variables: {
        assetId
      },
      skip: !isITProduct || !editable
    }
  );
  useEffect(() => {
    if (shareClientData) {
      const generateClientDropdown: any = getAllClients(
        shareClientData?.getITDocShareableClients
      );
      setClients(generateClientDropdown);
    }
  }, [shareClientData]);

  useEffect(() => {
    if (shareSiteData) {
      const generateSiteDropdown: any = getAllClients(
        shareSiteData?.getITDocShareableSites
      );
      setClients(generateSiteDropdown);
    }
  }, [shareSiteData]);

  const { data: shareRolesData, loading: shareRolesLoading } = useCachedQuery(
    GET_USER_ROLE_TYPES,
    {
      client: AccountClient,
      skip: !editable
    }
  );
  useEffect(() => {
    if (shareRolesData) {
      const generateRolesDropdown: any = getAllRoles(
        shareRolesData?.getUserRoleTypeRoles
      );
      setRoles(generateRolesDropdown);
    }
  }, [shareRolesData]);
  const validatePayload = () => {
    if (!editable) {
      return true;
    }
    const allAssetsList = cloneDeep(assetList);
    if (allAssetsList.length) {
      // client not configured
      const emptyClients = allAssetsList.filter((a) => {
        if (
          (a.clientSharedType !== ALL_CLIENTS_SHARED_TYPE &&
            !a.client?.accountId &&
            !isITProduct) ||
          (a.siteSharedType !== ALL_CLIENTS_SHARED_TYPE &&
            !a.site?.id &&
            isITProduct)
        ) {
          return true;
        }
      });
      if (emptyClients.length) {
        return false;
      }
      const emptyRoles = allAssetsList.filter(
        (a) =>
          a.userRoleSharedType !== ALL_ROLES_SHARED_TYPE && !a.roles?.length
      );
      if (emptyRoles.length) {
        return false;
      }
    }
    return true;
  };
  const getUpdateDifferencePayload = (updateAssetList = '') => {
    const updatedData = updateAssetList || assetList;
    let {
      addedItems: added,
      deletedItems: deleted,
      updatedItems: updated
    } = getParsedCrudData(docsList, updatedData, 'mappingId');
    let isAllClientAdded: boolean = false;
    const updatedDetails: UpdateRoleObj[] = [];
    if (added.length) {
      added = added.map((value: UpdateRoleObj) => {
        if (
          isITProduct
            ? value.siteSharedType === ALL_CLIENTS_SHARED_TYPE
            : value.clientSharedType === ALL_CLIENTS_SHARED_TYPE
        ) {
          isAllClientAdded = true;
        }
        return {
          clientSharedType: value.clientSharedType,
          siteSharedType: value.siteSharedType,
          userRoleSharedType: value.userRoleSharedType,
          accountId:
            value.clientSharedType === ALL_CLIENTS_SHARED_TYPE
              ? null
              : value.client?.accountId,
          siteId:
            value.siteSharedType === ALL_CLIENTS_SHARED_TYPE
              ? null
              : value.site?.id,
          addedRoleIds:
            value.userRoleSharedType === ALL_ROLES_SHARED_TYPE
              ? null
              : value.roles?.map((r) => r.roleId),
          deletedRoleIds: null, // deletedRole id will be always null for 'added' case
          mappingId: null // for new added items, the mapping id should be null
        };
      });
    }
    if (deleted.length) {
      deleted = deleted.map((value: UpdateRoleObj) => {
        return value?.mappingId;
      });
    }
    if (updated.length) {
      // need to calculate the added or deleted props, so recalculate again from updated items
      updated.forEach((updatedClient: UpdateRoleObj) => {
        let isClientUpdated: boolean = false;
        const baseClient = docsList.find(
          (a: UpdateRoleObj) => a.mappingId === updatedClient.mappingId
        );
        const updateRoleObjDetails: UpdateRoleObj = {
          accountId: updatedClient?.client?.accountId,
          siteId: updatedClient?.site?.id,
          clientSharedType: updatedClient.clientSharedType,
          siteSharedType: updatedClient.siteSharedType,
          userRoleSharedType: updatedClient.userRoleSharedType,
          addedRoleIds: updatedClient.addedRoleIds || null,
          deletedRoleIds: updatedClient.deletedRoleIds || null,
          mappingId: updatedClient.mappingId
        };
        const isClientOrSiteChanged = isITProduct
          ? baseClient?.site?.id !== updatedClient?.site?.id
          : baseClient?.client?.accountId !== updatedClient?.client?.accountId;
        if (isClientOrSiteChanged) {
          //  client updated
          const addedClient = {
            ...updateRoleObjDetails,
            addedRoleIds: updatedClient.roles?.map((r) => r.roleId),
            deletedRoleIds: null,
            mappingId: null, // for new added items, the mapping id should be null
            accountId: updatedClient?.client?.accountId || null,
            siteId: updatedClient?.site?.id || null
          };
          added.push(addedClient);
          isClientUpdated = true;
          const updatedSharedType = isITProduct
            ? updatedClient.siteSharedType
            : updatedClient.clientSharedType;
          if (updatedSharedType === ALL_CLIENTS_SHARED_TYPE) {
            isAllClientAdded = true;
          } else {
            deleted.push(baseClient?.mappingId);
          }
        }
        // roles updated
        /* if the client is updated no need to calculate the added/updated roles
         */
        if (!isClientUpdated) {
          const { deletedItems, addedItems } = getParsedCrudData(
            baseClient.roles || [],
            updatedClient.roles || [],
            'roleId'
          );
          if (deletedItems.length) {
            const removedRoleId = deletedItems.map(
              (role: RoleObj) => role.roleId
            );
            updateRoleObjDetails.deletedRoleIds = removedRoleId;
          }
          if (addedItems.length) {
            const addedRoleIds = addedItems.map((role: RoleObj) => role.roleId);
            updateRoleObjDetails.addedRoleIds = addedRoleIds;
          }
          if (deletedItems.length || addedItems.length) {
            updatedDetails.push(updateRoleObjDetails);
          }
        }
      });
    }
    if (isAllClientAdded) {
      added = added.filter((a: UpdateRoleObj) =>
        isITProduct
          ? a.siteSharedType === ALL_CLIENTS_SHARED_TYPE
          : a.clientSharedType === ALL_CLIENTS_SHARED_TYPE
      );
      /** In the existing mapping if the user selects All clients, then delete all other clients mapping */
      const deletedAllMapping = docsList.map((value: UpdateRoleObj) => {
        return value?.mappingId;
      });
      deleted.push(...deletedAllMapping);
    }
    const isValid = validatePayload();
    if (!isValid) {
      toast('warning', 'rmm.schedule.fill.required.fields');
      return;
    }
    return {
      added: added.length ? added : null,
      updated: updatedDetails.length ? updatedDetails : null,
      deleted: deleted.length ? deleted : null
    };
  };

  const generatePayloadAndShare = (updateAssetList = '') => {
    const sharePayload = getUpdateDifferencePayload(updateAssetList);
    if (sharePayload?.added || sharePayload?.deleted || sharePayload?.updated) {
      shareItDocument({
        variables: {
          assetId: assetId,
          input: sharePayload
        }
      });
    }
  };
  const handleClientChange = (newValue: DropDownObject, index: number) => {
    const updatedAssetData = cloneDeep(assetList);
    const currentIndex = index;
    if (newValue.value === ALL_CLIENTS_SHARED_TYPE) {
      updatedAssetData[currentIndex].client = null;
      updatedAssetData[currentIndex].clientSharedType = ALL_CLIENTS_SHARED_TYPE;
    } else {
      updatedAssetData[currentIndex].client = {
        accountId: newValue.value,
        name: newValue.name
      };
      updatedAssetData[currentIndex].clientSharedType = CLIENT_SHARED_TYPE;
    }
    setAssetList(updatedAssetData);
  };
  const handleSiteChange = (newValue: DropDownObject, index: number) => {
    const updatedAssetData = cloneDeep(assetList);
    const currentIndex = index;
    if (newValue.value === ALL_CLIENTS_SHARED_TYPE) {
      updatedAssetData[currentIndex].site = null;
      updatedAssetData[currentIndex].siteSharedType = ALL_CLIENTS_SHARED_TYPE;
    } else {
      updatedAssetData[currentIndex].site = {
        id: newValue.value,
        name: newValue.name
      };
      updatedAssetData[currentIndex].siteSharedType = CLIENT_SHARED_TYPE;
    }
    setAssetList(updatedAssetData);
  };
  const handleRoleChange = (newValue: DropDownObject[], index: number) => {
    const updatedAssetData = cloneDeep(assetList);
    const currentIndex = index;
    if (newValue?.find((r) => r.value === ALL_ROLES_SHARED_TYPE)) {
      updatedAssetData[currentIndex].roles = null;
      updatedAssetData[currentIndex].userRoleSharedType = ALL_ROLES_SHARED_TYPE;
    } else {
      updatedAssetData[currentIndex].roles =
        newValue?.map((r) => {
          return {
            roleId: r.value,
            name: r.name
          };
        }) || null;
      updatedAssetData[currentIndex].userRoleSharedType = ROLE_SHARED_TYPE;
    }
    setAssetList(updatedAssetData);
  };
  const getDropdownFormat = (name: Nullable<string>, value: string) => {
    if (!name) {
      return null;
    }
    return {
      name,
      value,
      label: name
    };
  };
  const addNewShareDoc = () => {
    const baseObj = {
      client: {
        name: '',
        accountId: ''
      },
      roles: [],
      mappingId: random(docsList.length, 15000)
    };
    setAssetList((prev: any) => [...prev, baseObj]);
  };
  const getSelectedClient = (
    account: ClientAccount,
    sharedType: string,
    site: Site
  ) => {
    if (sharedType === ALL_CLIENTS_SHARED_TYPE) {
      return getDropdownFormat(ALL_CLIENTS_LABEL, ALL_CLIENTS_SHARED_TYPE);
    }
    if (isITProduct) {
      return getDropdownFormat(site?.name, site?.id);
    }
    return getDropdownFormat(account?.name, account?.accountId);
  };
  const getSelectedRole = (role: RoleObj[], sharedType: string) => {
    if (sharedType === ALL_ROLES_SHARED_TYPE) {
      return getDropdownFormat(ALL_ROLES_LABEL, ALL_ROLES_SHARED_TYPE);
    }
    return role?.map((r) => {
      return getDropdownFormat(r.name, r.roleId);
    });
  };
  const getUniqueClients = (
    selectedClientList: string[],
    account: ClientAccount,
    site: Site
  ) => {
    if (selectedClientList.length) {
      if (isITProduct) {
        const uniqueSites = clients.filter((s: DropDownObject) => {
          if (site?.id === s.value) {
            return true;
          }
          return !selectedClientList.includes(s.value);
        });
        return uniqueSites;
      }
      const uniqueClients = clients.filter((s: DropDownObject) => {
        if (account?.accountId === s.value) {
          return true;
        }
        return !selectedClientList.includes(s.value);
      });
      return uniqueClients;
    }
    return clients;
  };
  const removeMapping = (mappingIndex: number) => {
    const updatedAssetData = cloneDeep(assetList);
    const currentIndex = mappingIndex;
    updatedAssetData.splice(currentIndex, 1);
    setAssetList(() => updatedAssetData);
    return updatedAssetData;
  };
  const removeSharedDocument = () => {
    const updateAssetList = removeMapping(deleteIndex);
    generatePayloadAndShare(updateAssetList);
  };

  return (
    <>
      <div className="monitor-table-view">
        {listLoader && <Loader />}
        {assetList.length ? (
          <>
            {editable && (
              <div className="p20 divider flex fx-sb">
                <div>
                  <Heading>
                    <FormattedMessage id="rmm.itdocument.shared.with" />
                  </Heading>
                </div>
                <div>
                  <ButtonGroup spacing="loose">
                    <Button plainSecondary onClick={toggleAssociateAsset}>
                      <FormattedMessage id="common.no" />
                    </Button>
                    <Button
                      primary
                      icon={<SVGIcon name="CircleWhiteTick" size="sm" />}
                      loading={shareLoading}
                      onClick={() => generatePayloadAndShare()}
                    >
                      <FormattedMessage id="common.yes" />
                    </Button>
                  </ButtonGroup>
                </div>
              </div>
            )}
            <div className="pt20 pb20 min-height-300 cursor-default">
              <ResourceList
                items={assetList || []}
                renderItem={(item: ITDocList) => {
                  const {
                    client,
                    roles,
                    clientSharedType,
                    siteSharedType,
                    userRoleSharedType,
                    site
                  } = item;
                  const sharedType = isITProduct
                    ? siteSharedType
                    : clientSharedType;
                  const index = assetList.findIndex(
                    (a: UpdateRoleObj) => a.mappingId === item.mappingId
                  );
                  const isAnyAllClientSelected =
                    assetList.find((ac: UpdateRoleObj) =>
                      isITProduct
                        ? ac.siteSharedType === ALL_CLIENTS_SHARED_TYPE
                        : ac.clientSharedType === ALL_CLIENTS_SHARED_TYPE
                    ) || false;
                  const selectedClient = getSelectedClient(
                    client,
                    sharedType,
                    site
                  );
                  const selectedRole = getSelectedRole(
                    roles,
                    userRoleSharedType
                  );
                  const uniqueClients = getUniqueClients(
                    selectedClients,
                    client,
                    site
                  );
                  if (selectedClient)
                    selectedClients.push(selectedClient.value);
                  /** when the user choose AllClient and if there are any other mapping, disable those mapping fields */
                  const isDisableElement =
                    (editable &&
                      isAnyAllClientSelected &&
                      sharedType !== ALL_CLIENTS_SHARED_TYPE) ||
                    shareRolesLoading ||
                    shareClientLoading ||
                    shareSiteLoading;
                  const makeAddNewMappingButtonVisilbe =
                    sharedType !== ALL_CLIENTS_SHARED_TYPE &&
                    (clients?.length > 1 || site?.length > 1);
                  const makeDeleteMappingButtonVisible = assetList.length > 1;
                  const uniqueKeyId = isITProduct
                    ? site?.id + index
                    : client?.accountId + index;
                  return (
                    <ResourceItem
                      id={uniqueKeyId}
                      gutter="md"
                      key={uniqueKeyId}
                      hideSelectedBgColor
                    >
                      <div className={`${editable ? '' : 'cursor-default'}`}>
                        <div className="flex asset-resource-list pl5 fx-sb">
                          <AccessControl
                            productToCheck={COMMON_CONSTANTS.MSP}
                            render={
                              <div className={`${editable ? 'w30' : 'w250'}`}>
                                <div className="flex-row flex-align-center item-select-container">
                                  <div className="w-35 flex actr jctr">
                                    <SVGIcon name="ClientLogo" />
                                  </div>
                                  {editable ? (
                                    <Select
                                      className="select-block w100 ml-10"
                                      onChange={(val: DropDownObject) => {
                                        handleClientChange(val, index);
                                      }}
                                      components={{
                                        IndicatorSeparator,
                                        DropdownIndicator
                                      }}
                                      menuPortalTarget={document.body}
                                      classNamePrefix="value_selector"
                                      placeholder={getTranslatedText(
                                        intl,
                                        'asset.sidebar.itDoc.chooseClient.pl'
                                      )}
                                      formatGroupLabel={formatGroupLabel}
                                      menuPlacement="auto"
                                      value={selectedClient}
                                      styles={{
                                        menuPortal: (base: any) => ({
                                          ...base,
                                          zIndex: 9999
                                        })
                                      }}
                                      options={uniqueClients}
                                      isDisabled={isDisableElement}
                                      isLoading={shareClientLoading}
                                    />
                                  ) : (
                                    <div className="ml-10">
                                      {sharedType === ALL_CLIENTS_SHARED_TYPE
                                        ? ALL_CLIENTS_LABEL
                                        : client?.name || 'NA'}
                                    </div>
                                  )}
                                </div>
                              </div>
                            }
                          />
                          <AccessControl
                            productToCheck={COMMON_CONSTANTS.IT}
                            render={
                              <div className={`${editable ? 'w30' : 'w250'}`}>
                                <div className="flex-row flex-align-center item-select-container">
                                  <div className="w-35 flex actr jctr">
                                    <SVGIcon name="Site" />
                                  </div>
                                  <div className="w100">
                                    {editable ? (
                                      <Select
                                        className="select-block w100 ml-10"
                                        onChange={(val: DropDownObject) => {
                                          handleSiteChange(val, index);
                                        }}
                                        components={{
                                          IndicatorSeparator,
                                          DropdownIndicator
                                        }}
                                        menuPortalTarget={document.body}
                                        classNamePrefix="value_selector"
                                        placeholder={getTranslatedText(
                                          intl,
                                          'asset.sidebar.itDoc.chooseSite.pl'
                                        )}
                                        formatGroupLabel={formatGroupLabel}
                                        menuPlacement="auto"
                                        value={selectedClient}
                                        styles={{
                                          menuPortal: (base: any) => ({
                                            ...base,
                                            zIndex: 9999
                                          })
                                        }}
                                        options={uniqueClients}
                                        isDisabled={isDisableElement}
                                        isLoading={shareClientLoading}
                                      />
                                    ) : (
                                      <div className="ml-10">
                                        {sharedType === ALL_CLIENTS_SHARED_TYPE
                                          ? ALL_CLIENTS_LABEL
                                          : site?.name || 'NA'}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            }
                          />
                          <div className="w50 ml-40">
                            <div className="w100">
                              <div className="flex-row item-select-container">
                                <div className="w-35 flex actr jctr">
                                  <SVGIcon name="ProjectLogo" />
                                </div>
                                {editable ? (
                                  <Select
                                    className="select-block w100 ml-10"
                                    onChange={(val: DropDownObject[]) => {
                                      handleRoleChange(val, index);
                                    }}
                                    components={{
                                      IndicatorSeparator,
                                      DropdownIndicator
                                    }}
                                    menuPortalTarget={document.body}
                                    classNamePrefix="value_selector"
                                    placeholder={getTranslatedText(
                                      intl,
                                      'client.details.user.chooseRole'
                                    )}
                                    isOptionDisabled={() =>
                                      userRoleSharedType ===
                                      ALL_ROLES_SHARED_TYPE
                                    }
                                    formatGroupLabel={formatGroupLabel}
                                    menuPlacement="auto"
                                    value={selectedRole}
                                    styles={{
                                      menuPortal: (base: any) => {
                                        return {
                                          ...base,
                                          zIndex: 9999
                                        };
                                      }
                                    }}
                                    options={shareableRoles}
                                    isDisabled={isDisableElement}
                                    isMulti
                                    isLoading={shareRolesLoading}
                                  />
                                ) : (
                                  <div className="ml-10">
                                    {userRoleSharedType ===
                                    ALL_ROLES_SHARED_TYPE
                                      ? ALL_ROLES_LABEL
                                      : roles?.map((r) => r.name).join(', ') ||
                                        'NA'}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <>
                            {editable ? (
                              <div className="mr-10 cp remove-asset-link mlauto flex mt4">
                                <ButtonGroup>
                                  <div
                                    className={`${
                                      makeDeleteMappingButtonVisible
                                        ? 'visibility-visible'
                                        : 'visibility-hidden'
                                    }`}
                                  >
                                    <Button
                                      plainSecondary
                                      icon={
                                        <SVGIcon
                                          name="CircleMinus"
                                          color="red !important"
                                        />
                                      }
                                      onClick={() => {
                                        removeMapping(index);
                                      }}
                                    />
                                  </div>
                                  <div
                                    className={`${
                                      makeAddNewMappingButtonVisilbe
                                        ? 'visibility-visible'
                                        : 'visibility-hidden'
                                    }`}
                                  >
                                    <Button
                                      disabled={isDisableElement}
                                      plainSecondary
                                      icon={
                                        <SVGIcon
                                          name="CirclePlus"
                                          color="green !important"
                                        />
                                      }
                                      onClick={() => {
                                        addNewShareDoc();
                                      }}
                                    />
                                  </div>
                                </ButtonGroup>
                              </div>
                            ) : (
                              <div>
                                <AccessControl
                                  roleToCheck="itDocumentation.edit"
                                  render={
                                    <Button
                                      plainSecondary
                                      icon={
                                        <SVGIcon
                                          name="delete"
                                          color="rgba(224, 32, 32, 1)"
                                        />
                                      }
                                      onClick={() => {
                                        showWarningWhileRemove(index);
                                      }}
                                    />
                                  }
                                />
                              </div>
                            )}
                          </>
                        </div>
                      </div>
                    </ResourceItem>
                  );
                }}
              />
            </div>
          </>
        ) : (
          <div className="flex full-height center jcenter mt25">
            <NoDataCard
              imageURL={`url(${envConfig.imageRoot}svg/not-found.svg)`}
              description={
                <FormattedMessage
                  id={getLokaliseKeyByProduct(productType, 'itShareEmtpy')}
                />
              }
              type="w400 pb20"
            />
          </div>
        )}
      </div>
      <Modal
        open={deleteModalActive}
        onClose={showWarningWhileRemove}
        variant="confirmation"
        title={getTranslatedText(intl, 'rmm.share.IT.doc.remove.heading')}
        noScroll
      >
        <div>
          <div className="mt-20">
            <FormattedMessage
              id={getLokaliseKeyByProduct(productType, 'shareITDoc')}
            />
          </div>
          <div className="mt-40 flex jend">
            <ButtonGroup spacing="loose">
              <Button plainSecondary onClick={() => showWarningWhileRemove()}>
                <FormattedMessage id="common.no" />
              </Button>
              <Button
                primary
                icon={<SVGIcon name="CircleWhiteTick" size="sm" />}
                loading={shareLoading}
                onClick={() => removeSharedDocument()}
              >
                <FormattedMessage id="common.yes" />
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </Modal>
      <Modal
        open={isClientChanged}
        onClose={() => setIsClientChanged(false)}
        variant="confirmation"
        title={getTranslatedText(
          intl,
          getLokaliseKeyByProduct(productType, 'reshareITDocHeading')
        )}
        primaryAction={{
          content: getTranslatedText(intl, 'common.close'),
          onAction: () => setIsClientChanged(false)
        }}
      >
        <div className="mt-20">
          <FormattedMessage
            id={getLokaliseKeyByProduct(productType, 'reshareITDocDesc')}
          />
        </div>
      </Modal>
    </>
  );
}

export default withErrorBoundary(injectIntl(withRouter(ShareItDocumentList)));
